import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../config/axios-instance';
import { 
  Layout, Menu, Input, Select, Button, Form, Card, Row, Col, 
  Progress, Upload, Badge, Tag, Space, Breadcrumb, Divider, 
  Checkbox, Typography, InputNumber, Tooltip, Modal, Avatar, message, Dropdown
} from 'antd';
import { 
  SaveOutlined, 
  FilePdfOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  SearchOutlined, 
  PictureOutlined,
  TagOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  CameraOutlined,
  CloudDownloadOutlined,
  DollarOutlined,
  CalendarOutlined,
  EditOutlined,
  SyncOutlined,
  UserOutlined,
  TeamOutlined,
  MenuOutlined,
  SendOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
// import Pusher from 'pusher-js';
import './ProductManager.css';
import { useSelector } from 'react-redux';



// Configuration locale après les imports - Removed hardcoded 127.0.0.1
// axiosInstance.defaults.baseURL = 'http://127.0.0.1:8000/api';


const { Header, Sider, Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const GENDERS = ['Women', 'Men', 'Unisex'];
const CATEGORIES = ['Jeans', 'Pants', 'Tops', 'Jacket', 'Shirt', 'Skirt', 'Dress'];
const FABRIC_TYPES = ['Denim', 'Cotton', 'Linen', 'Wool', 'Polyester', 'Silk', 'Viscose', 'Nylon', 'Recycled Denim'];
const FABRIC_NAMES = ['SIMON', 'ATLAS', 'BRISA', 'NATURA', 'KOBE'];
const FABRIC_COLORS = ['ATL ANTI CBL UE', 'BEIGE STONE', 'NATURAL SAND', 'WHITE', 'BLACK', 'NAVY'];
const MILLS = ['Dnm', 'SIM', 'HKT', 'VN', 'CALIK', 'ISKO', 'VICUNHA', 'KAIHARA'];
const TECHNIQUES = ['Fading', 'Pleat', 'Embroidery', 'Printing'];
const ACCESSORIES = ['Metal button', 'Horn button', 'Metal zipper', 'Plastic zipper', 'Rivets'];
const WASH_COLORS = ['Indigo', 'Black', 'Grey', 'Light Blue', 'Mid Blue', 'Dark Blue'];
const WASHES = ['Sustainable wash', 'Stone wash', 'Enzyme wash', 'Bleach wash', 'Acid wash', 'Rinse wash', 'Raw / Unwashed', 'Garment dye'];
const RESPONSIBLE_CHOICES = ['Organic Cotton', 'Recycled Polyester', 'BCI Cotton', 'Tencel', 'Hemp'];

const ProductManager = () => {
  const [form] = Form.useForm();
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);


  const [isNewSeasonFormVisible, setIsNewSeasonFormVisible] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollSeasons = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  
  const [dbOptions, setDbOptions] = useState<any>({
    genders: [],
    categories: [],
    seasons: [],
    fabricTypes: [],
    fabrics: [],
    mills: [],
    colors: [],
    techniques: [],
    accessories: [],
    washes: [],
    responsibleChoices: [],
    tags: []
  });

  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const { user } = useSelector((state: any) => state.auth);
  
  const [isSeasonModalVisible, setIsSeasonModalVisible] = useState(false);
  
  // Helper to check if user has a specific permission
  const hasPermission = (permission: string) => {
    return user?.permissions && Array.isArray(user.permissions) 
      ? user.permissions.includes(permission) 
      : false;
  };

  // Sauvegarder la saison sélectionnée dans localStorage
  useEffect(() => {
    if (selectedSeason) {
      localStorage.setItem('lastSelectedSeason', selectedSeason.toString());
    }
  }, [selectedSeason]);

  // Faire défiler jusqu'à la saison active
  useEffect(() => {
    if (selectedSeason && scrollRef.current) {
      setTimeout(() => {
        const activeElement = scrollRef.current?.querySelector('.pm-footer-season-item.active');
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 100);
    }
  }, [selectedSeason, dbOptions.seasons]);


  // Load options and products from Laravel on mount
  useEffect(() => {
    setSyncStatus('syncing');
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    
    // Charger les options
    axiosInstance.get('/manager/options', { headers })
      .then(res => {
        setDbOptions(res.data);
        


        // Sélectionner la saison sauvegardée ou la première par défaut
        const savedSeason = localStorage.getItem('lastSelectedSeason');
        const seasonId = savedSeason ? parseInt(savedSeason) : null;
        
        if (seasonId && res.data.seasons.some((s: any) => s.id === seasonId)) {
          setSelectedSeason(seasonId);
        } else if (res.data.seasons && res.data.seasons.length > 0) {
          setSelectedSeason(res.data.seasons[0].id);
        }
      })
      .catch(err => message.error('Failed to load database options'));


    // Charger les produits
    axiosInstance.get('/manager/products', { headers })
      .then(res => {
        setProducts(res.data);
        setSyncStatus('synced');
      })
      .catch(err => setSyncStatus('offline'));

  }, []);
  



  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'offline'>('synced');

  // Liste des produits initialisée à vide (sera remplie par l'API)
  const [products, setProducts] = useState<any[]>([]);

  // Selected products for publishing
  const [selectedProductIds, setSelectedProductIds] = useState<any[]>([]);

  // Clear selected products when season (collection) changes
  useEffect(() => {
    setSelectedProductIds([]);
  }, [selectedSeason]);




  // Polling for sync every 20 seconds
  useEffect(() => {
    let interval: any;
    if (selectedProduct && selectedProduct.id && !String(selectedProduct.id).startsWith('p')) {
      interval = setInterval(() => {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        
        axiosInstance.get(`/manager/products/${selectedProduct.id}`, { headers })
          .then(res => {
            const serverData = res.data;
            
            // Build update object only with fields NOT touched by the user
            const updateObj: any = {};
            Object.keys(serverData).forEach(key => {
              if (!form.isFieldTouched(key)) {
                if (key === 'tags') {
                  updateObj.tags = serverData.tags?.map((t: any) => t.id || t) || [];
                } else {
                  updateObj[key] = serverData[key];
                }
              }
            });

            form.setFieldsValue(updateObj);
            
            // Also update the products list to reflect changes in the sidebar/list
            setProducts(prev => prev.map(p => p.id === res.data.id ? res.data : p));
            setSyncStatus('synced');
          })
          .catch(err => {
            console.error("Polling sync error", err);
            setSyncStatus('offline');
          });
      }, 20000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedProduct, form]);

  // Polling for the entire products list every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      axiosInstance.get('/manager/products', { headers })
        .then(res => {
          setProducts(prev => {
            const unsavedTemp = prev.filter(p => String(p.id).startsWith('p'));
            const serverProducts = res.data.filter((sp: any) => !unsavedTemp.some(tp => tp.id === sp.id));
            return [...unsavedTemp, ...serverProducts];
          });
        })
        .catch(err => console.error("Products list sync error", err));
    }, 30000);

    return () => clearInterval(interval);
  }, []);



  useEffect(() => {
    if (selectedProduct) {
      // Préparer les données pour le formulaire
      const formData = {
        ...selectedProduct,
        // Si les tags sont des objets, on ne garde que les IDs
        tags: selectedProduct.tags?.map((t: any) => t.id || t) || []
      };
      form.setFieldsValue(formData);
    }
  }, [selectedProduct, form]);

  const getImageUrl = (path: any) => {
    if (!path || typeof path !== 'string') return null;
    if (path.startsWith('data:image')) return path;
    if (path.startsWith('http')) return path;
    
    // Get base domain from axiosInstance (strip /api if present)
    const baseDomain = axiosInstance.defaults.baseURL?.replace(/\/api$/, '') || 'http://localhost:8000';
    
    // Files on public disk are served via /storage/ prefix
    if (path.startsWith('images/')) return `${baseDomain}/storage/${path}`;
    // URL de votre serveur Laravel
    return `${baseDomain}/${path}`;
  };





  const handleSave = () => {
    setSyncStatus('syncing');
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    
    form.validateFields().then(values => {
      if (selectedProduct?.id && !String(selectedProduct.id).startsWith('p')) {
        // ONLY send touched fields to avoid overwriting changes made by colleagues
        const changedValues: any = {};
        Object.keys(values).forEach(key => {
          if (form.isFieldTouched(key)) {
            changedValues[key] = values[key];
          }
        });

        // If no fields were touched, skip the request
        if (Object.keys(changedValues).length === 0) {
          setSyncStatus('synced');
          return;
        }

        // UPDATE
        axiosInstance.put(`/manager/products/${selectedProduct.id}`, changedValues, { headers })
          .then(res => {
            setProducts(products.map(p => p.id === selectedProduct.id ? res.data : p));
            
            // Reset touched status for all fields after save
            const fields = form.getFieldsValue();
            const resetObj = Object.keys(fields).map(key => ({ name: key, touched: false }));
            form.setFields(resetObj);
            
            message.success('Product updated successfully!');
            setSyncStatus('synced');
          })
          .catch(() => setSyncStatus('offline'));
      } else {
        // CREATE
        axiosInstance.post('/manager/products', values, { headers })
          .then(res => {
            setProducts([...products.filter(p => p.id !== selectedProduct?.id), res.data]);
            setSelectedProduct(res.data);
            message.success('New product created!');
            setSyncStatus('synced');
          })
          .catch(() => setSyncStatus('offline'));
      }
    }).catch(() => {
      setSyncStatus('offline');
      message.error('Please fill in the required fields.');
    });
  };

  const handleCreateSeason = () => {
    if (!newSeasonName) return message.warning('Please enter a season name');
    
    // Vérifier si elle existe déjà dans dbOptions
    if (dbOptions.seasons.find((s: any) => s.name.toLowerCase() === newSeasonName.toLowerCase())) {
      return message.error('Season already exists');
    }
    
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    // Envoyer à Laravel (Route à créer dans api.php si pas encore fait)
    axiosInstance.post('/manager/seasons', { name: newSeasonName }, { headers })
      .then(res => {
        setDbOptions({
          ...dbOptions,
          seasons: [...dbOptions.seasons, res.data]
        });
        setNewSeasonName('');
        setIsNewSeasonFormVisible(false);
        message.success(`Season ${newSeasonName} created in database!`);
      })
      .catch(() => message.error('Failed to create season in backend'));
  };


  const handleDelete = () => {
    if (!selectedProduct) return;
    
    setSyncStatus('syncing');
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    axiosInstance.delete(`/manager/products/${selectedProduct.id}`, { headers })
      .then(() => {
        setProducts(products.filter(p => p.id !== selectedProduct.id));
        handleNewProduct();
        message.success('Product deleted successfully');
        setSyncStatus('synced');
      })
      .catch(() => setSyncStatus('offline'));
  };

  const handleReset = () => {
    if (selectedProduct) {
      form.setFieldsValue(selectedProduct);
    } else {
      form.resetFields();
    }
  };

  const handlePublishCollection = () => {
    if (!selectedSeason) {
      message.warning('Please select a collection first.');
      return;
    }

    const seasonName = dbOptions.seasons.find((s: any) => s.id === selectedSeason)?.name || 'this collection';
    const collectionProducts = products.filter(p => {
      if (Array.isArray(p.collection_id)) {
        return p.collection_id.some((c: any) => c.collection_id === selectedSeason);
      }
      return p.collection_id === selectedSeason;
    });

    if (collectionProducts.length === 0) {
      message.warning('No products in this collection to publish.');
      return;
    }

    const hasSelection = selectedProductIds.length > 0;
    const productsToPublish = hasSelection
      ? collectionProducts.filter(p => selectedProductIds.includes(p.id))
      : collectionProducts;

    if (productsToPublish.length === 0) {
      message.warning('None of the selected products exist in this collection.');
      return;
    }

    const title = hasSelection ? 'Publish Selected Products' : 'Publish Collection';
    const content = hasSelection
      ? `Are you sure you want to publish the ${productsToPublish.length} selected product(s) from "${seasonName}" to the product catalog? Products with existing codes will be skipped.`
      : `Are you sure you want to publish all ${productsToPublish.length} product(s) from "${seasonName}" to the product catalog? Products with existing codes will be skipped.`;

    Modal.confirm({
      title: title,
      content: content,
      okText: 'Publish',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: () => {
        setSyncStatus('syncing');
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const payload = hasSelection
          ? { collection_id: selectedSeason, product_ids: productsToPublish.map(p => p.id) }
          : { collection_id: selectedSeason };

        return axiosInstance
          .post('/manager/publish-collection', payload, { headers })
          .then(res => {
            const { published_count, skipped_count } = res.data;
            if (published_count > 0) {
              message.success(`${published_count} product(s) published successfully!${skipped_count > 0 ? ` ${skipped_count} skipped (already exist).` : ''}`);
            } else if (skipped_count > 0) {
              message.info(`All products already exist in the catalog. ${skipped_count} skipped.`);
            } else {
              message.info(res.data.message);
            }
            setSelectedProductIds([]);
            setSyncStatus('synced');
          })
          .catch(err => {
            console.error('Publish error:', err);
            message.error(err.response?.data?.message || 'Failed to publish collection.');
            setSyncStatus('offline');
          });
      },
    });
  };

  const handleNewProduct = () => {
    const tempId = 'p' + Date.now();
    const newProd = {
      id: tempId,
      name: 'Unnamed',
      code: '-',
      product_type: 'denim',
      type: 'DENIM',
      status: 'pending',
      collection_id: [{ collection_id: selectedSeason }]
    };
    
    setProducts([newProd, ...products]);
    setSelectedProduct(newProd);
    form.resetFields();
    form.setFieldsValue(newProd);
  };

  const filteredProducts = products
    .filter(p => {
      if (Array.isArray(p.collection_id)) {
        return p.collection_id.some((c: any) => c.collection_id === selectedSeason);
      }
      return p.collection_id === selectedSeason;
    })
    .filter(p => {
      if (activeFilter === 'Completed') return p.status === 'none';
      if (activeFilter === 'In Progress') return p.status === 'wip';
      if (activeFilter === 'Published') return !!p.published_at;
      return true;
    });

  const publishableProducts = filteredProducts.filter(p => !p.published_at);

  return (
    <Layout className="product-manager-container">
      <Sider width={320} className="pm-sider">
        <div className="pm-sidebar-header">
          <div className="pm-header-row">
            <span className="pm-section-label">Products</span>
            <div className="pm-sync-badge-container">
              <Badge status={syncStatus === 'synced' ? 'success' : syncStatus === 'syncing' ? 'processing' : 'error'} />
              <span className="pm-sync-text">{syncStatus === 'synced' ? 'Synchronisé' : syncStatus === 'syncing' ? 'Sync...' : 'Hors-ligne'}</span>
            </div>
          </div>
        </div>


        <div className="pm-filter-row">
          {['All', 'Completed', 'In Progress', 'Published'].map(f => {
            const count = products
              .filter(p => {
                if (Array.isArray(p.collection_id)) {
                  return p.collection_id.some((c: any) => c.collection_id === selectedSeason);
                }
                return p.collection_id === selectedSeason;
              })

              .filter(p => {
                if (f === 'Completed') return p.status === 'none';
                if (f === 'In Progress') return p.status === 'wip';
                if (f === 'Published') return !!p.published_at;
                return true;
              }).length;

            return (
              <div 
                key={f} 
                className={`pm-filter-chip-modern ${activeFilter === f ? 'active-manager' : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                <div className="pm-chip-label">{f}</div>
              </div>
            );
          })}
        </div>

        {/* Selection Bar */}
        <div className="pm-selection-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <Checkbox 
            checked={publishableProducts.length > 0 && publishableProducts.every(p => selectedProductIds.includes(p.id))}
            indeterminate={
              publishableProducts.some(p => selectedProductIds.includes(p.id)) && 
              !publishableProducts.every(p => selectedProductIds.includes(p.id))
            }
            onChange={(e) => {
              if (e.target.checked) {
                // Select all visible publishable products
                const visibleIds = publishableProducts.map(p => p.id);
                setSelectedProductIds(prev => {
                  const union = new Set([...prev, ...visibleIds]);
                  return Array.from(union);
                });
              } else {
                // Deselect all visible publishable products
                const visibleIds = publishableProducts.map(p => p.id);
                setSelectedProductIds(prev => prev.filter(id => !visibleIds.includes(id)));
              }
            }}
          >
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
              Select All ({publishableProducts.length})
            </span>
          </Checkbox>
          {selectedProductIds.length > 0 && (
            <span style={{ fontSize: '11px', color: '#182844', fontWeight: 700, background: '#e0f2fe', padding: '2px 8px', borderRadius: '12px' }}>
              {selectedProductIds.length} selected
            </span>
          )}
        </div>


                <div className="pm-product-list">
                  {filteredProducts.map(p => (
                    <div 
                      key={p.id} 
                      className={`pm-product-item ${selectedProduct?.id === p.id ? 'active-manager' : ''}`}
                      onClick={() => setSelectedProduct(p)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', width: '20px', justifyContent: 'center' }}>
                          {!p.published_at ? (
                            <Checkbox 
                              checked={selectedProductIds.includes(p.id)}
                              onChange={(e) => {
                                setSelectedProductIds(prev => 
                                  prev.includes(p.id) 
                                    ? prev.filter(id => id !== p.id) 
                                    : [...prev, p.id]
                                );
                              }}
                            />
                          ) : (
                            <Tooltip title="Published">
                              <GlobalOutlined style={{ color: '#10b981', fontSize: '15px' }} />
                            </Tooltip>
                          )}
                        </div>
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <span className="pm-product-name">{p.name}</span>
                            <span className="pm-product-code">{p.code}</span>
                            <span className="pm-product-type-badge">{p.type}</span>
                          </div>
                          <Tooltip title={p.status === 'none' ? 'Completed' : 'In Progress'}>
                            <div 
                              className={`pm-status-dot status-${p.status || 'pending'}`} 
                              style={{ 
                                background: p.status === 'none' ? '#10b981' : p.status === 'wip' ? '#f59e0b' : '#94a3b8' 
                              }} 
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

        <div className="pm-new-prod-container">
          {hasPermission('manager.product.create') && (
            <Button 
              type="dashed" 
              block 
              icon={<PlusOutlined />} 
              style={{ borderRadius: 12, height: 48, fontSize: 15, fontWeight: 500, color: '#475569' }}
              onClick={handleNewProduct}
            >
              New Product
            </Button>
          )}
        </div>
      </Sider>

      <Layout style={{ background: '#f8fafc' }}>
        <Header className="pm-content-header">
          <div className="pm-header-breadcrumb">
            <Tooltip title={dbOptions.seasons.find((s: any) => s.id === selectedSeason)?.name || 'Season'} placement="bottomLeft">
              <span className="pm-breadcrumb-season">
                {dbOptions.seasons.find((s: any) => s.id === selectedSeason)?.name || 'Season'}
              </span>
            </Tooltip>
            <span className="pm-breadcrumb-separator">›</span>
            <Tooltip title={selectedProduct ? selectedProduct.name : 'New Product'} placement="bottomLeft">
              <span className="pm-breadcrumb-name">
                {selectedProduct ? selectedProduct.name : 'New Product'}
              </span>
            </Tooltip>
            {selectedProduct?.code && (
              <span className="pm-breadcrumb-code" style={{ flexShrink: 0 }}>{selectedProduct.code}</span>
            )}
          </div>

          <div className="pm-header-actions">
            {/* Sync Status Indicator */}
           {/*  <div className={`pm-sync-badge-modern ${syncStatus}`}>
              <SyncOutlined spin={syncStatus === 'syncing'} />
              <span>{syncStatus === 'synced' ? 'SYNCED' : syncStatus === 'syncing' ? 'SYNCING' : 'OFFLINE'}</span>
            </div> */}
            {/* Collaborators Avatars */}


            <Divider type="vertical" style={{ height: 24, margin: '0 16px' }} />
            
            <Button className="pm-btn-action pm-btn-reset" onClick={handleReset}>Reset</Button>
            <Button 
              icon={<SendOutlined />} 
              className="pm-btn-action" 
              onClick={handlePublishCollection}
            >
              {selectedProductIds.length > 0 ? `Publish Selected (${selectedProductIds.length})` : 'Publish Collection'}
            </Button>
            <Button 
              icon={<DeleteOutlined />} 
              className="pm-btn-action pm-btn-delete"
              onClick={handleDelete}
              disabled={!selectedProduct}
            >
              Delete
            </Button>

            <Button type="primary" className="pm-btn-save" onClick={handleSave}>Save Changes</Button>
          </div>
        </Header>

        <Content className="pm-form-wrapper">
          <Form 
            form={form} 
            layout="vertical" 
            onValuesChange={(changedValues, allValues) => {
              
              // Sync changes to the sidebar list immediately
              if (selectedProduct) {
                setProducts(prev => prev.map(p => 
                  p.id === selectedProduct.id 
                    ? { ...p, ...changedValues, type: (changedValues.product_type || p.product_type || '').toUpperCase() } 
                    : p
                ));
              }
            }}
            initialValues={{ collection_id: [{ collection_id: selectedSeason }] }}
          >
            <Row gutter={24}>
              {/* Left Column */}
              <Col span={16}>
                {/* Identification Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon"><TagOutlined /></div>
                      <span className="pm-section-title-text">Identification</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
                  <Row gutter={24}>
                    <Col span={8}>
                      <Form.Item 
                        name="name" 
                        label={
                          <div className="pm-label-row">
                            <span className="pm-field-label">Product Name</span>
                            <span className="pm-required-badge">required</span>
                          </div>
                        }
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="Straight Jeans" className="pm-input-custom" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item 
                        name="code" 
                        label={
                          <div className="pm-label-row">
                            <span className="pm-field-label">Code</span>
                            <span className="pm-required-badge">required</span>
                          </div>
                        }
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="IDWF25JE1D" className="pm-input-custom" style={{ fontFamily: 'monospace' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item 
                        name="gender_id" 
                        label={
                          <div className="pm-label-row">
                            <span className="pm-field-label">Gender</span>
                            <span className="pm-required-badge">required</span>
                          </div>
                        }
                        rules={[{ required: true }]}
                      >
                        <Select 
                          showSearch 
                          optionFilterProp="children" 
                          placeholder="— Select —" 
                          className="pm-select-custom"
                          disabled={!hasPermission('manager.product.edit.id')}
                        >
                          {dbOptions.genders.map((g: any) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item name="category_id" label={<span className="pm-field-label">Category</span>} rules={[{ required: true }]}>
                        <Select 
                          showSearch 
                          optionFilterProp="children" 
                          placeholder="— Select —" 
                          className="pm-select-custom"
                          disabled={!hasPermission('manager.product.edit.id')}
                        >
                          {dbOptions.categories.map((c: any) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.List name="collection_id">
                        {(fields, { add, remove }) => (
                          <>
                            <div className="pm-label-row" style={{ marginBottom: 8 }}>
                              <span className="pm-field-label">Collections</span>
                              <span className="pm-required-badge">required</span>
                            </div>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space key={key} style={{ display: 'flex', marginBottom: 12 }} align="baseline">
                                <Form.Item
                                  {...restField}
                                  name={[name, 'collection_id']}
                                  rules={[{ required: true, message: 'Missing collection' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Select 
                                    placeholder="— Select —" 
                                    showSearch 
                                    optionFilterProp="children"
                                    className="pm-select-custom"
                                    style={{ width: 220 }}
                                  >
                                    {dbOptions.seasons.map((s: any) => (
                                      <Option key={s.id} value={s.id}>{s.name}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                <Button 
                                  type="text" 
                                  danger 
                                  icon={<MinusCircleOutlined />} 
                                  onClick={() => remove(name)} 
                                />
                              </Space>
                            ))}
                            <Button 
                              type="dashed" 
                              onClick={() => add()} 
                              block 
                              icon={<PlusOutlined />}
                              className="pm-btn-action"
                              style={{ height: 42, background: '#f8fafc' }}
                            >
                              Add Collection
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Col>
                    
                  </Row>
                  <Row gutter={24}>
<Col span={8}>
                      <Form.Item 
                        name="product_type" 
                        label={
                          <div className="pm-label-row">
                            <span className="pm-field-label">Type</span>
                            <span className="pm-required-badge">required</span>
                          </div>
                        }
                        rules={[{ required: true }]}
                      >
                        <Select 
                          showSearch 
                          optionFilterProp="children" 
                          placeholder="— Select —" 
                          className="pm-select-custom"
                          disabled={!hasPermission('manager.product.edit.id')}
                        >
                          <Option value="denim">Denim</Option>
                          <Option value="knit">Knit</Option>
                          <Option value="unisex">Unisex</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                </Card>

                {/* Description Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon" style={{ color: '#ef4444' }}><PictureOutlined /></div>
                      <span className="pm-section-title-text">Description</span>
                      <span className="pm-required-badge" style={{ marginLeft: 'auto' }}>required</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
                  <Form.Item name="description" noStyle rules={[{ required: true }]}>
                    <TextArea 
                      rows={4} 
                      className="pm-textarea-custom"
                      placeholder="High-waisted straight jeans made out of a rigid fabric with 100% Cotton..." 
                      maxLength={500}
                      disabled={!hasPermission('manager.product.edit.id')}
                    />
                  </Form.Item>
                  <Form.Item 
                    noStyle 
                    shouldUpdate={(prevValues, currentValues) => prevValues.description !== currentValues.description}
                  >
                    {() => (
                      <div className="pm-char-count">
                        {form.getFieldValue('description')?.length || 0} / 500 characters
                      </div>
                    )}
                  </Form.Item>
                </Card>

                {/* Material Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon" style={{ color: '#3b82f6' }}><ThunderboltOutlined /></div>
                      <span className="pm-section-title-text">Material & Manufacturing</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
            <Form.List name="fabric_id">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="pm-list-item-container" style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px dashed #e2e8f0' }}>
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'fabric_type_id']}
                            label={<span className="pm-field-label">Fabric Type</span>}
                          >
                            <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                              {dbOptions.fabricTypes.map((t: any) => (
                                <Option key={t.id} value={t.id}>{t.name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'fabric_id']}
                            label={<span className="pm-field-label">Fabric Name</span>}
                          >
                            <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                              {dbOptions.fabrics.map((f: any) => (
                                <Option key={f.id} value={f.id}>{f.name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'fabric_mill_id']}
                            label={<span className="pm-field-label">Fabric Mill</span>}
                          >
                            <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                              {dbOptions.mills.map((m: any) => (
                                <Option key={m.id} value={m.id}>{m.name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'fabric_color_id']}
                            label={<span className="pm-field-label">Fabric Color</span>}
                          >
                            <Input placeholder="Enter fabric color..." className="pm-input-custom" />
                          </Form.Item>
                        </Col>
                        <Col span={2} style={{ display: 'flex', alignItems: 'center', paddingTop: 32 }}>
                          <Button 
                            type="text" 
                            danger 
                            icon={<MinusCircleOutlined />} 
                            onClick={() => remove(name)} 
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Button 
                    type="dashed" 
                    onClick={() => add()} 
                    block 
                    icon={<PlusOutlined />}
                    className="pm-btn-action"
                    style={{ height: 44, background: '#f8fafc', marginTop: 8 }}
                  >
                    Add Another Fabric
                  </Button>
                </>
              )}
            </Form.List>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.List name="technic_id">
                        {(fields, { add, remove }) => (
                          <>
                            <span className="pm-blue-label" style={{ marginBottom: 12 }}>Techniques</span>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={12} align="middle" style={{ marginBottom: 12 }}>
                                <Col span={14}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'technic_id']}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                                      {dbOptions.techniques.map((t: any) => (
                                        <Option key={t.id} value={t.id}>{t.name}</Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'technic_cost']}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber 
                                      placeholder="0.00" 
                                      className="pm-input-custom" 
                                      style={{ width: '100%', textAlign: 'right', fontWeight: 700 }} 
                                      precision={2}
                                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button 
                                    type="text" 
                                    danger 
                                    icon={<MinusCircleOutlined />} 
                                    onClick={() => remove(name)} 
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Button 
                              type="dashed" 
                              onClick={() => add()} 
                              block 
                              icon={<PlusOutlined />}
                              className="pm-btn-action"
                              style={{ height: 40, background: '#f8fafc' }}
                            >
                              Add Technique
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Col>
                    <Col span={12}>
                      <Form.List name="accessory_id">
                        {(fields, { add, remove }) => (
                          <>
                            <span className="pm-blue-label" style={{ marginBottom: 12 }}>Accessories</span>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={8} align="middle" style={{ marginBottom: 12 }}>
                                <Col span={10}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'accessory_id']}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                                      {dbOptions.accessories.map((a: any) => (
                                        <Option key={a.id} value={a.id}>{a.name}</Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'accessory_cost']}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber 
                                      placeholder="0.00" 
                                      className="pm-input-custom" 
                                      style={{ width: '100%', textAlign: 'right', fontWeight: 700 }} 
                                      precision={2}
                                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'accessory_unit']}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select placeholder="Unit" className="pm-select-custom">
                                      <Option value="piece">Piece</Option>
                                      <Option value="meter">Meter</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button 
                                    type="text" 
                                    danger 
                                    icon={<MinusCircleOutlined />} 
                                    onClick={() => remove(name)} 
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Button 
                              type="dashed" 
                              onClick={() => add()} 
                              block 
                              icon={<PlusOutlined />}
                              className="pm-btn-action"
                              style={{ height: 40, background: '#f8fafc' }}
                            >
                              Add Accessory
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>
                </Card>

                {/* Wash Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon" style={{ color: '#0ea5e9' }}></div>
                      <span className="pm-section-title-text">Wash</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
                 <Form.List name="wash_id">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ borderBottom: '1px dashed #e2e8f0', marginBottom: 20, paddingBottom: 20 }}>
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'wash_id']}
                            label={<span className="pm-field-label">Wash Name</span>}
                          >
                            <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                              {dbOptions.washes.map((w: any) => (
                                <Option key={w.id} value={w.id}>{w.name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'wash_color']}
                            label={<span className="pm-field-label">Wash Colors</span>}
                          >
                            <Select placeholder="— Select —" showSearch optionFilterProp="children" className="pm-select-custom">
                              {dbOptions.washcolors.map((c: any) => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'eim_score']}
                            label={<span className="pm-field-label">EIM Score</span>}
                          >
                            <InputNumber placeholder="0" min={0} max={100} className="pm-input-custom" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'wash_cost']}
                            label={<span className="pm-field-label">Wash Cost (€)</span>}
                          >
                            <InputNumber 
                              placeholder="0.00" 
                              className="pm-input-custom" 
                              style={{ width: '100%', textAlign: 'right', fontWeight: 700 }} 
                              precision={2}
                              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={2} style={{ display: 'flex', alignItems: 'center', paddingTop: 32 }}>
                          <Button 
                            type="text" 
                            danger 
                            icon={<MinusCircleOutlined />} 
                            onClick={() => remove(name)} 
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Button 
                    type="dashed" 
                    onClick={() => add()} 
                    block 
                    icon={<PlusOutlined />}
                    className="pm-btn-action"
                    style={{ height: 44, background: '#f8fafc', marginTop: 8 }}
                  >
                    Add Another Wash
                  </Button>
                </>
              )}
            </Form.List>
                </Card>
                {/* Price Structure Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon" style={{ background: '#fef3c7', color: '#d97706' }}></div>
                      <span className="pm-section-title-text">Price Structure</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
                  <Row gutter={24} align="middle">
                    <Col span={12}>
                      <Form.Item name="is_3d" valuePropName="checked" noStyle>
                        <Checkbox>
                          <span className="pm-checkbox-label">3D Product</span>
                        </Checkbox>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="responsible_choice_id" label={<span className="pm-field-label">Responsible Choice</span>}>
                        <Select showSearch optionFilterProp="children" placeholder="— Select —" className="pm-select-custom" allowClear>
                          {dbOptions.responsibleChoices.map((c: any) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.product_type !== curr.product_type}>
                    {({ getFieldValue }) => {
                      const productType = getFieldValue('product_type');
                      if (productType === 'knit') {
                        return (
                          <Row gutter={24} style={{ marginTop: 16 }}>
                            <Col span={12}>
                              <Form.Item name="bci_price" label={<span className="pm-field-label">BCI Price (€)</span>}>
                                <InputNumber 
                                  min={0} 
                                  step={0.01} 
                                className="pm-input-custom" 
                                  style={{ width: '100%', textAlign: 'right', fontWeight: 700, fontSize: 16 }} 
                                  placeholder="0.00" 
                                  disabled={!hasPermission('manager.product.edit.price')}
                                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item name="cotton_price" label={<span className="pm-field-label">Cotton Price (€)</span>}>
                                <InputNumber 
                                  min={0} 
                                  step={0.01} 
                                  className="pm-input-custom" 
                                  style={{ width: '100%', textAlign: 'right', fontWeight: 700, fontSize: 16 }} 
                                  placeholder="0.00" 
                                  disabled={!hasPermission('manager.product.edit.price')}
                                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>

                  <div className="pm-price-row">
                    <div className="pm-label-row">
                      <span className="pm-price-label">Price (€)</span>
                      <span className="pm-required-badge">required</span>
                    </div>
                    <Form.Item name="price" noStyle rules={[{ required: true }]}>
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        className="pm-input-custom" 
                        placeholder="12.35"
                        disabled={!hasPermission('manager.product.edit.price')}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                       /*  parser={value => value!.replace(/\$\s?|(,*)/g, '')} */
                        style={{ width: 120, textAlign: 'right', fontWeight: 700, fontSize: 18 }}
                      />
                    </Form.Item>
                  </div>

                </Card>

              </Col>

              {/* Right Column */}
              <Col span={8}>
                {/* Completion Progress Card */}
                 {/* Visuals Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon" style={{ color: '#10b981' }}><PictureOutlined /></div>
                      <span className="pm-section-title-text">Product Visuals</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
                  <Form.Item shouldUpdate={(prev, curr) => 
                    prev.image_front !== curr.image_front || 
                    prev.image_back !== curr.image_back || 
                    prev.image_left !== curr.image_left || 
                    prev.image_right !== curr.image_right
                  } noStyle>
                    {() => {
                      const values = form.getFieldsValue();
                      
                      const handleUpload = (field: string) => (file: any) => {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          form.setFields([{ name: field, value: e.target?.result, touched: true }]);
                        };
                        reader.readAsDataURL(file);
                        return false; // Prevent actual upload request
                      };

                      return (
                        <div className="pm-image-upload-grid">
                          <Form.Item name="image_front" noStyle>
                            <Upload 
                              showUploadList={false} 
                              listType="picture-card" 
                              className={`pm-upload-wrapper-custom ${values.image_front ? 'active-manager' : ''}`}
                              beforeUpload={handleUpload('image_front')}
                              disabled={!hasPermission('manager.product.edit.images')}
                            >
                              {values.image_front ? (
                                <img src={getImageUrl(values.image_front) || ""} alt="Front" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }} />
                              ) : (
                                <div className="pm-upload-content">
                                  <CameraOutlined className="pm-upload-icon" />
                                  <span className="pm-upload-text-sub">Front</span>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>
                          
                          <Form.Item name="image_back" noStyle>
                            <Upload 
                              showUploadList={false} 
                              listType="picture-card" 
                              className={`pm-upload-wrapper-custom ${values.image_back ? 'active-manager' : ''}`}
                              beforeUpload={handleUpload('image_back')}
                              disabled={!hasPermission('manager.product.edit.images')}
                            >
                              {values.image_back ? (
                                <img src={getImageUrl(values.image_back) || ""} alt="Back" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }} />
                              ) : (
                                <div className="pm-upload-content">
                                  <CameraOutlined className="pm-upload-icon" />
                                  <span className="pm-upload-text-sub">Back</span>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>

                          <Form.Item name="image_left" noStyle>
                            <Upload 
                              showUploadList={false} 
                              listType="picture-card" 
                              className={`pm-upload-wrapper-custom ${values.image_left ? 'active-manager' : ''}`}
                              beforeUpload={handleUpload('image_left')}
                              disabled={!hasPermission('manager.product.edit.images')}
                            >
                              {values.image_left ? (
                                <img src={getImageUrl(values.image_left) || ""} alt="Left" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }} />
                              ) : (
                                <div className="pm-upload-content">
                                  <CameraOutlined className="pm-upload-icon" />
                                  <span className="pm-upload-text-sub">Left</span>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>

                          <Form.Item name="image_right" noStyle>
                            <Upload 
                              showUploadList={false} 
                              listType="picture-card" 
                              className={`pm-upload-wrapper-custom ${values.image_right ? 'active-manager' : ''}`}
                              beforeUpload={handleUpload('image_right')}
                              disabled={!hasPermission('manager.product.edit.images')}
                            >
                              {values.image_right ? (
                                <img src={getImageUrl(values.image_right) || ""} alt="Right" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }} />
                              ) : (
                                <div className="pm-upload-content">
                                  <CameraOutlined className="pm-upload-icon" />
                                  <span className="pm-upload-text-sub">Right</span>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>
                        </div>
                      );
                    }}
                  </Form.Item>

                  <Form.Item name="link_360" label={<span className="pm-360-link-label">360° Link</span>}>
                    <Input 
                      className="pm-input-custom" 
                      placeholder="https://360.demcointer.com/fw25/part1-denim/idwf25je1" 
                      disabled={!hasPermission('manager.product.edit.images')}
                    />
                  </Form.Item>
                </Card>



               
                {/* Tags Section */}
                <Card 
                  title={
                    <div className="pm-section-header-custom">
                      <div className="pm-section-icon"><TagOutlined /></div>
                      <span className="pm-section-title-text">Tags</span>
                    </div>
                  } 
                  className="pm-section-card"
                >
                  <Form.Item name="tags" noStyle rules={[{ type: 'array', max: 5, message: 'Maximum 5 tags allowed' }]}>
                    <Select
                      mode="multiple"
                      showSearch
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="— Select —"
                      className="pm-select-custom pm-tags-select"
                      optionFilterProp="children"
                      onChange={(val: any) => {
                        if (val && val.length > 5) {
                          form.setFieldsValue({ tags: val.slice(0, 5) });
                          message.warning('Maximum 5 tags allowed');
                        }
                      }}
                    >
                      {dbOptions.tags.map((tag: any) => (
                        <Option key={tag.id} value={tag.id}>
                          {tag.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <div className="pm-tag-help-text" style={{ marginTop: 8, color: '#94a3b8', fontSize: 12 }}>
                    Assign existing categories or attributes to this product.
                  </div>
                </Card>

              </Col>
            </Row>
          </Form>
        </Content>
      </Layout>


      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CalendarOutlined style={{ fontSize: 18, color: '#1e293b' }} />
            <span style={{ fontSize: 20, fontWeight: 700 }}>Seasons</span>
          </div>
        }
        visible={isSeasonModalVisible}
        onCancel={() => setIsSeasonModalVisible(false)}
        footer={[
          <div key="footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={{ color: '#94a3b8', fontSize: 14 }}>3 seasons</span>
            {!isNewSeasonFormVisible && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                className="pm-btn-new-season"
                onClick={() => setIsNewSeasonFormVisible(true)}
              >
                New season
              </Button>
            )}
          </div>
        ]}
        width={600}
        bodyStyle={{ padding: '24px' }}
        className="pm-modal-custom"
        closeIcon={<span style={{ fontSize: 20 }}>×</span>}
      >
        {isNewSeasonFormVisible && (
          <div className="pm-new-season-form">
            <div className="pm-new-season-title">New Season</div>
            
            <div style={{ marginBottom: 20 }}>
              <span className="pm-modal-label">Season Name</span>
              <Input 
                className="pm-input-custom" 
                placeholder="E.G., FW26, SS27..." 
                value={newSeasonName}
                onChange={e => setNewSeasonName(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <span className="pm-modal-label">Line / Type</span>
              <Select showSearch optionFilterProp="children" placeholder="— Select —" className="pm-select-custom" style={{ width: '100%' }} defaultValue="DENIM">
                <Option value="DENIM">DENIM</Option>
                <Option value="KNIT">KNIT</Option>
                <Option value="WOVEN">WOVEN</Option>
              </Select>
            </div>

            <div className="pm-modal-actions">
              <Button className="pm-btn-modal-cancel" onClick={() => setIsNewSeasonFormVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" className="pm-btn-modal-create" onClick={handleCreateSeason}>
                Create
              </Button>
            </div>
          </div>
        )}

        <div className="pm-season-modal-card">
          <div className="pm-season-card-header">
            <span className="pm-season-card-title">FW25</span>
            <div className="pm-season-active-dot" />
            <span className="pm-season-active-text">active</span>
          </div>
          <div className="pm-season-card-line">Line: Denim</div>
          <div className="pm-season-product-count">4 products</div>
          <div className="pm-season-actions">
            <Button className="pm-btn-action-sm edit" icon={<EditOutlined />} />
            <Button className="pm-btn-action-sm" icon={<DeleteOutlined />} />
          </div>
        </div>

        <div className="pm-season-modal-card">
          <div className="pm-season-card-header">
            <span className="pm-season-card-title">SS25</span>
          </div>
          <div className="pm-season-card-line">Line: Knit</div>
          <div className="pm-season-product-count">1 product</div>
          <div className="pm-season-actions">
            <Button className="pm-btn-action-sm edit" icon={<EditOutlined />} />
            <Button className="pm-btn-action-sm" icon={<DeleteOutlined />} />
          </div>
        </div>

        <div className="pm-season-modal-card">
          <div className="pm-season-card-header">
            <span className="pm-season-card-title">SS26</span>
          </div>
          <div className="pm-season-card-line">Line: Denim</div>
          <div className="pm-season-product-count">0 products</div>
          <div className="pm-season-actions">
            <Button className="pm-btn-action-sm edit" icon={<EditOutlined />} />
            <Button className="pm-btn-action-sm" icon={<DeleteOutlined />} />
          </div>
        </div>
      </Modal>

      {/* Barre des Saisons Horizontale en Bas */}
      <div className="pm-fixed-footer">
        <div className="pm-footer-seasons-scroll" ref={scrollRef}>
      

          {dbOptions.seasons.map((s: any) => (
            <div 
              key={s.id} 
              className={`pm-footer-season-item ${selectedSeason === s.id ? 'active' : ''}`}
              onClick={() => setSelectedSeason(s.id)}
            >
              <span className="pm-footer-season-name">{s.name}</span>
              <span className="pm-footer-season-arrow">▾</span>
            </div>
          ))}
          <div className="pm-footer-manage-btn" onClick={() => setIsSeasonModalVisible(true)}>
            <PlusOutlined />
          </div>
        </div>
        <div className="pm-footer-nav-arrows">
              <Dropdown
            overlay={
              <Menu 
                className="pm-seasons-dropdown-menu"
                onClick={({ key }) => setSelectedSeason(Number(key))}
                selectedKeys={[String(selectedSeason)]}
              >
                {dbOptions.seasons.map((s: any) => (
                  <Menu.Item key={s.id}>{s.name}</Menu.Item>
                ))}
              </Menu>
            }
            placement="topLeft"
            trigger={['click']}
          >
            <div className="pm-footer-menu-btn">
              <MenuOutlined />
            </div>
          </Dropdown>
          <span className="pm-nav-arrow" onClick={() => scrollSeasons('left')}>‹</span>
          <span className="pm-nav-arrow" onClick={() => scrollSeasons('right')}>›</span>
        </div>
      </div>

    </Layout>

  );
};

export default ProductManager;
